import { path } from 'static-path';
import { objectToSearchString } from 'use-query-params';

// # Routing notes
// See below for various routes in use

// ## Root
// `/knowledge` entry path for the knowledge app.
export const knowledgeManagementPath = path('/knowledge');
//           ^?

// ## Collection paths
// `/knowledge/collection` checks to see if you have a collection then redirects accordingly to create or else edit existing.
export const collectionRootPath = knowledgeManagementPath.path('/collection'); // checks for a collection then redirects to either create or edit
//           ^?
// `/knowledge/collection/:collectionId` collection edit/management screen.
const collectionEditPath = knowledgeManagementPath.path(
  `/collection/:collectionId`,
);

// ## Settings paths
export const knowledgeSettingsPath = knowledgeManagementPath.path('/settings');
//           ^?
// `/knowledge/settings/ai-suggestions` the draft of the AI article suggestions settings.
export const aiSuggestionsSettingsPath =
  knowledgeSettingsPath.path('/ai-suggestions');
// `/knowledge/settings/ai-suggestions/versions` every saved version of them.
export const aiSuggestionsVersionsPath =
  aiSuggestionsSettingsPath.path('/versions');
// `/knowledge/settings/ai-suggestions/versions/:versionId` one saved version, read-only.
export const aiSuggestionsVersionPath =
  aiSuggestionsVersionsPath.path('/:versionId');

// ## Article paths
// `/knowledge/article-editor/:articleId` article editor page.
// `/knowledge/article-editor/:articleId?categoryId=:categoryId` alternative article editor screen with a 'back to collection' link.
const articleEditPath = knowledgeManagementPath.path(
  //           ^?
  `/article-editor/:articleId`,
);
export const articleVersionHistoryPath =
  articleEditPath.path(`/version-history`);
const viewArticlesPath = knowledgeManagementPath.path('/articles');

// ## Article suggestions
// `/knowledge/article-suggestions` review queue for the drafts the AI writes.
const articleSuggestionsPath = knowledgeManagementPath.path(
  '/article-suggestions',
);
//           ^?

export const internalKbPath = path('/internal-kb');

const internalKbCollectionPath = internalKbPath.path(
  '/collection/:collectionId',
);
const internalKbArticlePath = internalKbPath.path(
  '/collection/:collectionId/article/:articleId',
);

export const generalSettingsPath = path('/settings'); // The agent-interface settings path
export const helpCentersPath = generalSettingsPath.path('/help-centers');
export const helpCenterSettingsPath = helpCentersPath.path('/:helpCenterId');

export const routeGenerator = {
  content: (): string => collectionRootPath({}),
  articleSuggestions: (): string => articleSuggestionsPath({}),
  articles: (search: string | undefined): string => {
    if (!search?.length) return viewArticlesPath({});
    return `${viewArticlesPath({})}?${search}`;
  },
  collectionEdit: ({
    collectionId,
    articlesFiltersStore,
  }: {
    collectionId: string;
    // AppFilterStore but only needs to be object, with optional collectionId
    articlesFiltersStore: {
      collectionId?: string[] | null; // Singular name as this is what will be used in the URL for each collectionId selected
    };
  }): string => {
    // remove collectionId as it's already in the path
    const { collectionId: _, ...withoutCollectionId } = articlesFiltersStore;
    const searchString = objectToSearchString(withoutCollectionId);
    if (!searchString.length) return collectionEditPath({ collectionId });
    return `${collectionEditPath({ collectionId })}?${searchString}`;
  },
  articleVersionHistory: ({
    articleId,
    languageId,
  }: {
    articleId: string;
    languageId?: string;
  }): string => {
    const base = articleVersionHistoryPath({ articleId });
    if (languageId) return `${base}?langId=${languageId}`;
    return base;
  },
  articleEdit: ({
    articleId,
    collectionId,
    languageId,
  }: {
    articleId: string;
    collectionId?: string;
    languageId?: string;
  }): string => {
    const params: Partial<{ langId: string; collectionId: string }> = {};
    if (languageId) params['langId'] = languageId;
    if (collectionId) params['collectionId'] = collectionId;

    const searchParams =
      Object.keys(params).length > 0
        ? new URLSearchParams(params).toString()
        : '';

    const editArticlePathWithQuery = `${articleEditPath({
      articleId,
    })}${searchParams.length ? `?${searchParams}` : ''}`;

    return editArticlePathWithQuery;
  },
  knowledgeSettingsPath: (): string => knowledgeSettingsPath({}),
  aiSuggestionsSettings: (): string => aiSuggestionsSettingsPath({}),
  aiSuggestionsVersions: (): string => aiSuggestionsVersionsPath({}),
  aiSuggestionsVersion: ({ versionId }: { versionId: string }): string =>
    aiSuggestionsVersionPath({ versionId }),
  internalKBDashboard: (): string => internalKbPath({}),
  internalKBCollection: ({ collectionId }: { collectionId: string }): string =>
    internalKbCollectionPath({ collectionId }),
  internalKBArticle: ({
    collectionId,
    articleId,
    languageId,
  }: {
    collectionId: string;
    articleId: string;
    languageId: string;
    // languageId: LanguageId;
  }): { path: string; search: string } => ({
    path: internalKbArticlePath({ collectionId, articleId }),
    search: `langId=${languageId}`,
  }),
  helpCentersPath: (): string => helpCentersPath({}),
  helpCenterSettingsPath: ({
    helpCenterId,
  }: {
    helpCenterId: string;
  }): string => helpCenterSettingsPath({ helpCenterId }),
};
